import { Box, Button, ResponsiveContext, Text } from 'grommet';
import { Trash, Video } from 'grommet-icons';
import { Project } from 'lib/api';
import { observer } from 'mobx-react-lite';
import { useContext } from 'react';
import { useCommercialStore, useUsageStore } from 'src/context';
import { shortenCurrency, USD } from 'src/utils/currency';
import { getAnimProps } from 'src/utils/styling';
import { AddNewButton } from '../AddNewButton';
import { Divider } from '../Divider';
import { SaveStatus, SaveStatusIndicator } from '../SaveStatusIndicator';

export type UsageEstimatorHeaderProps = {
  project?: Project;
  onCreateCommercial: () => void;
  onDeleteCommercials: () => void;
  hasSelectedCommercials?: boolean;
  totalWages?: number;
  grandTotal?: number;
  height?: string;
};

export const UsageEstimatorHeader = observer(
  ({
    project,
    onCreateCommercial,
    onDeleteCommercials,
    hasSelectedCommercials,
    totalWages,
    grandTotal,
    height = '92px',
  }: UsageEstimatorHeaderProps) => {
    /** Context **/
    const commercialStore = useCommercialStore();
    const usageStore = useUsageStore();
    const screenSize = useContext(ResponsiveContext);
    const isDesktop = screenSize === 'large';

    /** Computed **/
    const saveStatus = usageStore.isSaving || commercialStore.isSaving ? SaveStatus.Saving : SaveStatus.Saved;

    /** Render **/
    return (
      <Box
        background="light-3"
        flex="grow"
        direction="row"
        align="center"
        justify="between"
        height={height}
        gap="medium"
        style={{ zIndex: 1 }}
        elevation="small"
      >
        <Box direction="row" width="67%" gap="large" align="center">
          <Box direction="column" gap="xsmall">
            <Box
              margin={{ left: 'medium' }}
              animation={getAnimProps(0, 'slideUp')}
              justify="start"
              direction="row"
              pad={{ right: 'none' }}
            >
              <Text truncate size="xlarge">
                {project ? `${project.name} Usages` : 'Usage Estimator'}
              </Text>
            </Box>
            <Divider height="2px" animation={{ type: 'slideRight', size: 'xlarge', duration: 500 }} />
            {saveStatus && (
              <Box align="end" animation={getAnimProps(0, 'slideDown')} pad={{ right: 'none' }}>
                <SaveStatusIndicator saveStatus={saveStatus} boxProps={{ margin: 'none' }} />
              </Box>
            )}
          </Box>
          <Box
            justify="center"
            height="50px"
            round="1.5rem"
            border={{ size: '1px', color: 'light-4' }}
            elevation="small"
          >
            <AddNewButton
              onClick={onCreateCommercial}
              style={{
                padding: '15px',
                borderRadius: '1.5rem',
                fontSize: '0.9rem',
                fontWeight: 500,
              }}
              label="New Commercial"
              plain
              icon={<Video color="text" size="1.2rem" />}
            />
          </Box>
          <Button
            onClick={() => onDeleteCommercials()}
            icon={<Trash color="red" />}
            disabled={!hasSelectedCommercials}
            tip="Delete Selected Commercials"
          />
        </Box>

        <Box direction="row" width="32%">
          <Box direction="column" gap="xsmall" justify="end" align="start" width="50%">
            <Box animation={getAnimProps(100, 'slideUp', 500)}>
              <Text size="small" weight={500}>
                TOTAL WAGES
              </Text>
            </Box>
            <Divider animation={{ type: 'slideLeft', size: 'xlarge', duration: 500 }} />
            <Box animation={getAnimProps(100, 'slideDown', 500)}>
              <Text truncate size="large" weight={500}>
                {(!isDesktop ? shortenCurrency : USD)(totalWages)}
              </Text>
            </Box>
          </Box>
          <Box direction="column" gap="xsmall" justify="end" align="start" width="50%">
            <Box animation={getAnimProps(0, 'slideUp', 500)}>
              <Text size="small" weight={700}>
                GRAND TOTAL
              </Text>
            </Box>
            <Divider animation={{ type: 'slideLeft', size: 'xlarge', duration: 500 }} />
            <Box animation={getAnimProps(0, 'slideDown', 500)}>
              <Text truncate size="large" weight={700}>
                {(!isDesktop ? shortenCurrency : USD)(grandTotal)}
              </Text>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }
);
